<script setup>
import { computed, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { getUnixTime } from 'date-fns';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import ScheduleMessageModal from 'dashboard/components/ScheduleMessageModal.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const description = ref('');
const showScheduleModal = ref(false);

const getFollowUps = useMapGetter('followUps/getFollowUps');
const uiFlags = useMapGetter('followUps/getUIFlags');

const conversationId = computed(() => Number(props.conversationId));

const followUps = computed(() => getFollowUps.value(conversationId.value));
const pendingFollowUps = computed(() =>
  followUps.value.filter(followUp => followUp.status === 'pending')
);
const resolvedFollowUps = computed(() =>
  followUps.value.filter(followUp => followUp.status !== 'pending')
);

watch(
  conversationId,
  newId => {
    if (newId) {
      store.dispatch('followUps/get', { conversationId: newId });
    }
  },
  { immediate: true }
);

const scheduledTime = followUp =>
  messageTimestamp(followUp.scheduled_at, 'LLL d, h:mm a');

const statusLabel = followUp =>
  t(`CONVERSATION.FOLLOW_UP.STATUS.${followUp.status}`);

const openScheduleModal = () => {
  if (!description.value.trim()) return;
  showScheduleModal.value = true;
};

const hideScheduleModal = () => {
  showScheduleModal.value = false;
};

const onScheduleChosen = async scheduledTimeValue => {
  hideScheduleModal();
  if (!scheduledTimeValue) return;
  try {
    await store.dispatch('followUps/create', {
      conversationId: conversationId.value,
      description: description.value,
      scheduledAt: getUnixTime(scheduledTimeValue),
    });
    description.value = '';
    useAlert(t('CONVERSATION.FOLLOW_UP.CREATED_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.FOLLOW_UP.CREATE_ERROR'));
  }
};

const markDone = followUp => {
  store.dispatch('followUps/complete', {
    conversationId: conversationId.value,
    id: followUp.id,
  });
};

const cancel = followUp => {
  store.dispatch('followUps/cancel', {
    conversationId: conversationId.value,
    id: followUp.id,
  });
};
</script>

<template>
  <div class="flex flex-col gap-3 px-1 py-1">
    <div class="flex flex-col gap-2">
      <textarea
        v-model="description"
        rows="2"
        class="w-full reset-base text-sm px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-black1 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
        :placeholder="$t('CONVERSATION.FOLLOW_UP.INPUT_PLACEHOLDER')"
      />
      <div class="flex justify-end">
        <NextButton
          sm
          icon="i-ph-alarm"
          :label="$t('CONVERSATION.FOLLOW_UP.SCHEDULE')"
          :disabled="!description.trim() || uiFlags.isCreating"
          @click="openScheduleModal"
        />
      </div>
    </div>

    <div v-if="pendingFollowUps.length" class="flex flex-col gap-2">
      <div
        v-for="followUp in pendingFollowUps"
        :key="followUp.id"
        class="flex flex-col gap-1 p-2 rounded-lg bg-n-alpha-1"
      >
        <p class="text-sm text-n-slate-12 m-0">{{ followUp.description }}</p>
        <span class="flex items-center gap-1 text-xs text-n-slate-11">
          <Icon icon="i-ph-alarm" class="size-3" />
          {{
            $t('CONVERSATION.FOLLOW_UP.SCHEDULED_FOR', {
              time: scheduledTime(followUp),
            })
          }}
        </span>
        <div class="flex items-center gap-1 mt-1">
          <NextButton
            xs
            ghost
            teal
            icon="i-ph-check-circle"
            :label="$t('CONVERSATION.FOLLOW_UP.MARK_DONE')"
            @click="markDone(followUp)"
          />
          <NextButton
            xs
            ghost
            ruby
            icon="i-ph-x-circle"
            :label="$t('CONVERSATION.FOLLOW_UP.CANCEL')"
            @click="cancel(followUp)"
          />
        </div>
      </div>
    </div>

    <p
      v-else-if="!followUps.length"
      class="text-sm text-n-slate-11 text-center py-2 m-0"
    >
      {{ $t('CONVERSATION.FOLLOW_UP.EMPTY_STATE') }}
    </p>

    <div v-if="resolvedFollowUps.length" class="flex flex-col gap-1">
      <div
        v-for="followUp in resolvedFollowUps"
        :key="followUp.id"
        class="flex items-center justify-between gap-2 px-2 py-1"
      >
        <span class="text-xs text-n-slate-11 line-clamp-1">
          {{ followUp.description }}
        </span>
        <span class="text-xs font-medium text-n-slate-10 flex-shrink-0">
          {{ statusLabel(followUp) }}
        </span>
      </div>
    </div>

    <woot-modal v-model:show="showScheduleModal" :on-close="hideScheduleModal">
      <ScheduleMessageModal
        :title="$t('CONVERSATION.FOLLOW_UP.SCHEDULE_TITLE')"
        @close="hideScheduleModal"
        @choose-time="onScheduleChosen"
      />
    </woot-modal>
  </div>
</template>
